import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../utils/cn';

// Served from /public so it can be preloaded from index.html — it is the LCP element.
const logo = '/logo.png';
const logoDark = '/logo-white.png';

const Navbar = () => {
      const [isOpen, setIsOpen] = useState(false);
      const { isDark, toggleTheme } = useTheme();
      const location = useLocation();

      const navLinks = [
            { name: 'Home', path: '/' },
            {
                  name: 'Product',
                  path: '/features',
                  megaMenu: [
                        {
                              title: 'Core Features',
                              items: [
                                    { name: 'School Management', path: '/school-management-system', description: 'Connected school administration and ERP' },
                                    { name: 'Features', path: '/features', description: 'Explore all school workflows' },
                                    { name: 'LMS', path: '/best-learning-management-system-lahore-karachi-pakistan', description: 'Learning management for schools' },
                                    { name: 'Student Information', path: '/student-information-system', description: 'Admissions, records and reporting' },
                                    { name: 'Fees', path: '/school-fee-management-software', description: 'Vouchers and fee collection' },
                                    { name: 'Attendance', path: '/school-attendance-system', description: 'Attendance records and follow-up' },
                              ]
                        },
                        {
                              title: 'More',
                              items: [
                                    { name: 'Security', path: '/security', description: 'Data protection and privacy' },
                                    { name: 'Download App', path: '/download-app', description: 'Mobile application for all users' },
                                    { name: 'About Us', path: '/about-us', description: 'Our mission and values' },
                                    { name: 'Why Ilmversity', path: '/why-ilmversity', description: 'Why schools choose us' },
                              ]
                        }
                  ]
            },
            { name: 'Success Stories', path: '/case-studies' },
            { name: 'Resources', path: '/resources' },
            { name: 'Contact', path: '/contact' },
      ];

      return (
            <nav className="fixed top-0 w-full z-50 glass dark:glass-dark border-b border-white/20 dark:border-white/10">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between h-16">
                              <div className="flex items-center">
                                    <Link to="/" className="flex items-center">
                                          <img src={logo} alt="Ilmversity" width="124" height="40" fetchPriority="high" decoding="async" className="h-10 w-auto dark:hidden" />
                                          {/* White silhouette of the same logo for dark mode; the navy wordmark vanishes on the dark background. */}
                                          <img src={logoDark} alt="Ilmversity" width="124" height="40" loading="lazy" decoding="async" className="h-10 w-auto hidden dark:block" />
                                    </Link>
                              </div>

                              <div className="hidden md:block">
                                    <div className="ml-10 flex items-baseline space-x-4">
                                          {navLinks.map((link) => (
                                                link.megaMenu ? (
                                                      <div key={link.name} className="relative group">
                                                            <Link
                                                                  to={link.path}
                                                                  className={cn(
                                                                        "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                                                                        location.pathname.startsWith('/features') ||
                                                                              location.pathname === '/best-learning-management-system-lahore-karachi-pakistan' ||
                                                                              location.pathname === '/security' ||
                                                                              location.pathname === '/download-app'
                                                                              ? "bg-primary/10 text-primary-700 dark:bg-white/10 dark:text-white"
                                                                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                                                                  )}
                                                            >
                                                                  {link.name}
                                                            </Link>
                                                            <div className="absolute left-0 mt-2 w-[600px] rounded-xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 p-6 grid grid-cols-2 gap-8 transform origin-top-left">
                                                                  {link.megaMenu.map((section) => (
                                                                        <div key={section.title}>
                                                                              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                                                                                    {section.title}
                                                                              </h3>
                                                                              <div className="space-y-4">
                                                                                    {section.items.map((item) => (
                                                                                          <Link
                                                                                                key={item.name}
                                                                                                to={item.path}
                                                                                                className="group/item block"
                                                                                          >
                                                                                                <div className="text-sm font-medium text-gray-900 dark:text-white group-hover/item:text-primary transition-colors">
                                                                                                      {item.name}
                                                                                                </div>
                                                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                                                      {item.description}
                                                                                                </p>
                                                                                          </Link>
                                                                                    ))}
                                                                              </div>
                                                                        </div>
                                                                  ))}
                                                            </div>
                                                      </div>
                                                ) : (
                                                      <Link
                                                            key={link.name}
                                                            to={link.path}
                                                            className={cn(
                                                                  "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200",
                                                                  location.pathname === link.path
                                                                        ? "bg-primary/10 text-primary-700 dark:bg-white/10 dark:text-white"
                                                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                                                            )}
                                                      >
                                                            {link.name}
                                                      </Link>
                                                )
                                          ))}
                                    </div>
                              </div>

                              <div className="hidden md:flex items-center space-x-4">
                                    <button
                                          onClick={toggleTheme}
                                          aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                                          className="p-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-300"
                                    >
                                          {isDark ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-slate-400" />}
                                    </button>
                                    <Link
                                          to="/request-free-trial"
                                          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg hover:shadow-primary/20"
                                    >
                                          Request Free Trial
                                    </Link>
                              </div>

                              <div className="md:hidden flex items-center">
                                    <button
                                          onClick={toggleTheme}
                                          aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                                          className="p-2 mr-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-300"
                                    >
                                          {isDark ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-slate-400" />}
                                    </button>
                                    <button
                                          onClick={() => setIsOpen(!isOpen)}
                                          aria-label={isOpen ? 'Close main menu' : 'Open main menu'}
                                          aria-expanded={isOpen}
                                          className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-200 hover:text-primary focus:outline-none"
                                    >
                                          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                                    </button>
                              </div>
                        </div>
                  </div>

                  {/* Mobile menu */}
                  {isOpen && (
                        <div className="md:hidden glass dark:glass-dark border-t border-white/10">
                              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                                    {navLinks.map((link) => (
                                          link.megaMenu ? (
                                                <div key={link.name} className="space-y-1">
                                                      <Link
                                                            to={link.path}
                                                            onClick={() => setIsOpen(false)}
                                                            className={cn(
                                                                  "block px-3 py-2 rounded-md text-base font-medium",
                                                                  location.pathname === link.path
                                                                        ? "bg-primary/10 text-primary-700 dark:bg-white/10 dark:text-white"
                                                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                                                            )}
                                                      >
                                                            {link.name}
                                                      </Link>
                                                      <div className="pl-4 space-y-4 py-2">
                                                            {link.megaMenu.map((section) => (
                                                                  <div key={section.title} className="space-y-2">
                                                                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3">
                                                                              {section.title}
                                                                        </div>
                                                                        {section.items.map((item) => (
                                                                              <Link
                                                                                    key={item.name}
                                                                                    to={item.path}
                                                                                    onClick={() => setIsOpen(false)}
                                                                                    className={cn(
                                                                                          "block px-3 py-2 rounded-md text-sm",
                                                                                          location.pathname === item.path
                                                                                                ? "bg-primary/10 text-primary-700 dark:bg-white/10 dark:text-white"
                                                                                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"
                                                                                    )}
                                                                              >
                                                                                    <div className="font-medium">{item.name}</div>
                                                                                    <div className="text-xs opacity-70">{item.description}</div>
                                                                              </Link>
                                                                        ))}
                                                                  </div>
                                                            ))}
                                                      </div>
                                                </div>
                                          ) : (
                                                <Link
                                                      key={link.name}
                                                      to={link.path}
                                                      onClick={() => setIsOpen(false)}
                                                      className={cn(
                                                            "block px-3 py-2 rounded-md text-base font-medium",
                                                            location.pathname === link.path
                                                                  ? "bg-primary/10 text-primary-700 dark:bg-white/10 dark:text-white"
                                                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                                                      )}
                                                >
                                                      {link.name}
                                                </Link>
                                          )
                                    ))}
                                    <Link
                                          to="/request-free-trial"
                                          onClick={() => setIsOpen(false)}
                                          className="block w-full text-center mt-4 bg-primary text-white px-4 py-2 rounded-lg font-medium"
                                    >
                                          Request Free Trial
                                    </Link>
                              </div>
                        </div>
                  )}
            </nav>
      );
};

export default Navbar;
